/**
 * Zod schemas for YoY admin API responses.
 *
 * These schemas validate the runtime shape of data coming from the backend.
 * They intentionally mirror the TypeScript interfaces in dashboard.ts — any
 * drift between the two surfaces as a ZodError at the API boundary rather
 * than a silent type cast deep inside a component.
 *
 * Scope: YoY hooks only. Other dashboard types (ExportStats, UtilizationData,
 * PerformanceData) are migrated in follow-up PRs when those hooks move to
 * React Query.
 */

import { z } from "zod";

// ── YoY Summary ──────────────────────────────────────────────────────────────
// Mirrors: YoYPeriodSummary, YoYSummary in dashboard.ts

const yoYPeriodSummarySchema = z.object({
  total_billed: z.number(),
  total_hours: z.number(),
  weeks: z.number(),
  months: z.number(),
  avg_weekly_billed: z.number(),
  avg_weekly_hours: z.number(),
  avg_monthly_billed: z.number(),
  avg_monthly_hours: z.number(),
});

export const yoySummarySchema = z.object({
  before: yoYPeriodSummarySchema.nullable(),
  after: yoYPeriodSummarySchema.nullable(),
  goLiveDate: z.string().nullable(),
  afterStart: z.string().nullable(),
  afterEnd: z.string(),
  beforeStart: z.string().nullable(),
  beforeEnd: z.string().nullable(),
});

// ── YoY Time Series ──────────────────────────────────────────────────────────
// Mirrors: YoYDataPoint in dashboard.ts

export const yoyDataPointSchema = z.object({
  year: z.number(),
  period_num: z.number(),
  prof_id: z.string(),
  prof_name: z.string(),
  hours: z.number(),
  billed: z.number(),
  period: z.enum(["Before", "After"]),
});

export const yoyTimeSeriesSchema = z.array(yoyDataPointSchema);

// ── Daily View ───────────────────────────────────────────────────────────────
// Mirrors: DailyDataPoint, DailyViewData in dashboard.ts

const dailyDataPointSchema = z.object({
  date: z.string(),
  hours: z.number(),
});

export const dailyViewSchema = z.object({
  after: z.array(dailyDataPointSchema),
  before: z.array(dailyDataPointSchema),
  goLiveDate: z.string().nullable(),
});

// ── Go-Live Dates ─────────────────────────────────────────────────────────────
// Mirrors: GoLiveRecord in dashboard.ts

export const goLiveRecordSchema = z.object({
  prof_id: z.string(),
  go_live_date: z.string(),
});

export const goLiveRecordArraySchema = z.array(goLiveRecordSchema);

// ── Professionals ─────────────────────────────────────────────────────────────
// Mirrors: Professional in utils/adminHelpers.ts

export const professionalSchema = z.object({
  prof_id: z.string(),
  full_name: z.string(),
  email: z.string().optional(),
});

export const professionalsSchema = z.array(professionalSchema);
